import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { SphereLabel } from "@components/common/sphere-label";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { ProjectLevelIntegrationDialog } from "@custom-types/integrations-types";
import { Dialog, useToast } from "@flat-ui";
import { Divider, Stack, Typography } from "@mui/material";
import { ConnectDialogAutodeskHint } from "@pages/project-details/project-integrations/connect-dialog-autodesk-hint";
import { ConnectDialogProcoreHint } from "@pages/project-details/project-integrations/connect-dialog-procore-hint";
import { IntegrationAutocomplete } from "@pages/project-details/project-integrations/integration-autocomplete";
import { IntegrationsService } from "@services/integrations-service/integrations-service";
import { REAUTHORIZATION_NOT_COMPLETED } from "@services/integrations-service/integrations-service-constants";
import {
  IntegrationCompany,
  IntegrationProject,
  ProjectIntegrationsMap,
} from "@services/integrations-service/integrations-service-types";
import { PROCORE_ERROR_MESSAGES } from "@services/integrations-service/procore/procore-constants";
import { INTEGRATION_INFO } from "@src/constants/integrations-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { memo, useEffect, useState } from "react";

/** Display name for the projects container of an integration */
const PROJECT_CONTAINER_LABEL: Record<FaroCoreApiTypes.IntegrationId, string> =
  {
    autodesk: "hub",
    procore: "company",
  };

/** Text to display when there are no project containers */
const NO_CONTAINERS_LABEL: Record<FaroCoreApiTypes.IntegrationId, string> = {
  autodesk: "No hubs",
  procore: "No companies",
};

interface Props {
  /** Integrations service instance */
  integrationsService: IntegrationsService;

  /** Integration ID */
  integrationId: FaroCoreApiTypes.IntegrationId;

  /** Callback to set the project integrations */
  setProjectIntegrations(projectIntegrations: ProjectIntegrationsMap): void;

  /** Callback to set the dialog state */
  setDialogState(dialog: ProjectLevelIntegrationDialog): void;
}

/** Renders project integration connect dialog */
function ProjectIntegrationsConnectDialogComponent({
  integrationsService,
  integrationId,
  setProjectIntegrations,
  setDialogState,
}: Props): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();

  const [isFetchingCompanies, setIsFetchingCompanies] =
    useState<boolean>(false);
  const [companies, setCompanies] = useState<IntegrationCompany[]>([]);
  const [selectedCompany, setSelectedCompany] =
    useState<IntegrationCompany | null>(null);

  const [isFetchingProjects, setIsFetchingProjects] = useState<boolean>(false);
  const [projects, setProjects] = useState<IntegrationProject[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<IntegrationProject | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcoreAppError, setIsProcoreAppError] = useState<boolean>(false);
  const shouldShowProcoreAppHint =
    integrationId === "procore" && isProcoreAppError;
  const shouldShowAutodeskAppHint = integrationId === "autodesk";

  async function onConnectConfirm(
    selectedIntegrationProject: IntegrationProject
  ): Promise<void> {
    trackEvent({
      name: ProjectEvents.connectIntegration,
      props: { integrationId },
    });

    try {
      setIsLoading(true);
      const updatedProjectIntegrations =
        await integrationsService.connectProject(selectedIntegrationProject);
      setProjectIntegrations(updatedProjectIntegrations);
      setDialogState("connected");
    } catch (error) {
      handleErrorWithToast({
        id: `connectProject-${Date.now().toString()}`,
        title: `Error connecting project to ${INTEGRATION_INFO[integrationId].displayName}`,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  // Fetch companies of integration provider
  useEffect(() => {
    async function fetchCompanies(): Promise<void> {
      setIsFetchingCompanies(true);

      try {
        const companies = await integrationsService.getIntegrationCompanies(
          integrationId
        );

        setCompanies(companies);
      } catch (error) {
        // If the error is "reauthorization_not_completed" then just show a warning instead
        const message = getErrorDisplayMarkup(error);
        if (message === REAUTHORIZATION_NOT_COMPLETED) {
          openToast({
            title: "Unable to continue with the project connection.",
            message: "The integration reauthorization couldn't be completed.",
            variant: "warning",
          });
        } else {
          handleErrorWithToast({
            id: `fetchCompanies-${Date.now().toString()}`,
            title: "Error fetching integration workspaces",
            error,
          });
        }
      }

      setIsFetchingCompanies(false);
    }

    void fetchCompanies();
  }, [handleErrorWithToast, integrationsService, integrationId, openToast]);

  // Fetch projects under selected company of integration provider
  useEffect(() => {
    async function fetchProjects(): Promise<void> {
      if (!selectedCompany) {
        return;
      }

      setIsFetchingProjects(true);

      try {
        const projects = await integrationsService.getIntegrationProjects(
          selectedCompany
        );

        setProjects(projects);
      } catch (error) {
        // If the error is "reauthorization_not_completed" then just show a warning instead
        const message = getErrorDisplayMarkup(error);
        if (message === REAUTHORIZATION_NOT_COMPLETED) {
          openToast({
            title: "Unable to continue with the project connection.",
            message: "The integration reauthorization couldn't be completed.",
            variant: "warning",
          });
        } else {
          handleErrorWithToast({
            id: `fetchProjects-${Date.now().toString()}`,
            title: "Error fetching integration projects",
            error,
          });
        }

        // Extra error check for Procore. if the error message contains
        // "App is not connected to this company" is most probable that the
        // Sphere XG app is not installed in that Procore account.
        // Show a hint to the user in this case.
        if (message.includes(PROCORE_ERROR_MESSAGES.appNotConnected)) {
          setIsProcoreAppError(true);
        }
      }

      setIsFetchingProjects(false);
    }

    void fetchProjects();
  }, [handleErrorWithToast, integrationsService, selectedCompany, openToast]);

  function handleCompanyChange(value: IntegrationCompany | null): void {
    setSelectedCompany(value);
    setSelectedProject(null);
  }

  function handleProjectChange(value: IntegrationProject | null): void {
    setSelectedProject(value);
  }

  async function handleOnConfirm(): Promise<void> {
    if (selectedProject) {
      await onConnectConfirm(selectedProject);
    }
  }

  return (
    <Dialog
      open={true}
      showSpinner={isLoading}
      isConfirmDisabled={!selectedCompany || !selectedProject}
      onConfirm={() => void handleOnConfirm()}
      confirmText="Enable"
      title={`Let's enable ${INTEGRATION_INFO[integrationId].displayName} in this project`}
      onCancel={() => setDialogState("none")}
    >
      <Stack>
        <Typography
          sx={{
            fontSize: DEFAULT_INPUT_FONT_SIZE,
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          Select the <var>{INTEGRATION_INFO[integrationId].displayName}</var>{" "}
          project you would like to sync with this project.
        </Typography>

        <Divider sx={{ marginBottom: SPACE_ELEMENTS_OF_MODAL }} />

        {shouldShowProcoreAppHint && <ConnectDialogProcoreHint />}
        {shouldShowAutodeskAppHint && <ConnectDialogAutodeskHint />}

        {/* Workspace autocomplete */}
        <SphereLabel
          title={`${INTEGRATION_INFO[integrationId].displayName} ${PROJECT_CONTAINER_LABEL[integrationId]}`}
        />
        <IntegrationAutocomplete
          value={selectedCompany}
          options={companies}
          noOptionsText={NO_CONTAINERS_LABEL[integrationId]}
          onChange={handleCompanyChange}
          placeholder={`Select ${PROJECT_CONTAINER_LABEL[integrationId]}`}
          shouldFocus={true}
          shouldDisabled={isFetchingCompanies}
        />

        {/* Project autocomplete */}
        <SphereLabel
          title={
            <>
              <var style={{ fontWeight: "inherit" }}>
                {INTEGRATION_INFO[integrationId].displayName}
              </var>{" "}
              project
            </>
          }
          sx={{ marginTop: SPACE_ELEMENTS_OF_MODAL }}
        />
        <IntegrationAutocomplete
          value={selectedProject}
          options={projects}
          noOptionsText="No projects"
          placeholder="Select project"
          onChange={handleProjectChange}
          shouldDisabled={!selectedCompany || isFetchingProjects}
        />
      </Stack>
    </Dialog>
  );
}

export const ProjectIntegrationsConnectDialog = memo(
  ProjectIntegrationsConnectDialogComponent
);
